import AddModal from "./AddModal";
import { useEffect, useState } from "react";
import Filter from "./Filter";
import { exportHCCReport, getHCCDropdownOptions, getHCCSpecialityData, deleteHCCRecord } from "../../services/hccService";
import { imageIdGenerate } from "../../utils/custom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import CustomPagination from "../Manager/ManagerWorkLists/CustomPagination";
import EditModal from "./EditModal";
import moment from "moment";
import UploadReportModal from "./UploadReportModal";
import { getUserDetail } from "../../services/userService";
import Crypto from 'crypto-js';
import Swal from "sweetalert2";
import { userRole } from "../../utils/constants";
import configurationAPI from '../../apis/configuration/configurationAPI';

function HCCProject() {
    const [showAddModal, setShowAddModal] = useState(false);
    const [hccDropdownOptions, setHccDropdownOptions] = useState({});
    const [showEditModal, setShowEditModal] = useState(false);
    const [hccSpecialityData, setHccSpecialityData] = useState([]);
    const [filters, setFilters] = useState({});
    const [invalidateList, setInvalidateList] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRow, setSelectedRow] = useState();
    const [isExporting, setIsExporting] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [deletingRecordId, setDeletingRecordId] = useState();
    const [user, setUser] = useState({});
    const [customFieldIdsForValidation, setCustomFieldIdsForValidation] = useState([]);
    const [hccFields, setHCCFields] = useState([]);
    const [hccFieldsValue, setHCCFieldsValue] = useState([]);
    const size = 10;
    const role = localStorage.getItem('Role');

    const closeAddModal = () => setShowAddModal(false);
    const openAddModal = () => setShowAddModal(true);
    const openEditModal = () => setShowEditModal(true);
    const closeEditModal = () => {
        setSelectedRow('');
        setShowEditModal(false);
    }
    const openUploadModal = () => setShowUploadModal(true);
    const closeUploadModal = () => setShowUploadModal(false);

    const fetchHCCDropdownOptions = async () => {
        const res = await getHCCDropdownOptions();
        if (res.success) {
            setHccDropdownOptions(res.data);
        }
    };

    const fetchUserDetails = async () => {
        try {
            const res = await getUserDetail();
            if (res.success) {
                setUser(res.data);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const fetchHCCSpecialityData = async () => {
        try {
            const res = await getHCCSpecialityData({ page: currentPage, size, filters });
            if (res.success) {
                setTotalCount(res?.data?.count);
                const decryptData = res?.data?.records?.map(hcc => ({
                    id: hcc.memberId ? decryptMember(hcc.memberId) : hcc.memberId,
                    name: hcc.memberName ? decryptMember(hcc.memberName) : hcc.memberName,
                    mrNo: hcc.mrNo ? decryptMember(hcc.mrNo) : hcc.mrNo,
                    medicareNo: hcc.medicareNo ? decryptMember(hcc.medicareNo) : hcc.medicareNo
                }));

                const data = res?.data?.records?.map((item, index) => ({
                    ...item,
                    memberId: decryptData[index].id,
                    memberName: decryptData[index].name,
                    mrNo: decryptData[index].mrNo,
                    medicareNo: decryptData[index].medicareNo
                }))
                setHccSpecialityData(data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const fetchExportHCCReport = async () => {
        try {
            setIsExporting(true);
            const res = await exportHCCReport(filters);
            if (res.success && res.data?.data) {
                const bufferArray = new Uint8Array(res?.data?.data);
                const blob = new Blob([bufferArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "HCCSpecialityReport.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.log(error);
        }
        setIsExporting(false);
    };
   
    useEffect(() => {
        fetchHCCDropdownOptions();
        fetchUserDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchHCCSpecialityData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invalidateList, currentPage, size, filters]);

    function decryptMember(encrypted) {
        const decrypted = Crypto.AES.decrypt(encrypted, process.env.REACT_APP_SECRET_PASS);
        return decrypted.toString(Crypto.enc.Utf8);
    }

    const handleDeleteRecord = (id) => {
        if (!id) return;
        Swal.fire({
            text: "Are you sure you would like to delete this record?",
            icon: "warning",
            showCancelButton: true,
            buttonsStyling: false,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            customClass: {
                confirmButton: "btn btn-warning",
                cancelButton: "btn btn-active-light",
            },
        }).then(async function (result) {
            if (result?.value === true) {
                try {
                    setDeletingRecordId(id);
                    const res = await deleteHCCRecord(id);
                    if (res?.success) {
                        window.toastr.success(res?.message);
                        setInvalidateList(prev => !prev);
                    } else {
                        window.toastr.error(res?.message)
                    }
                } catch (error) {
                    window.toastr.error(error?.response?.data?.message)
                }
                setDeletingRecordId(null);
            }
        });
    }

    const fetchHCCFields = async () => {
        try {
            const res = await configurationAPI.getHCCFields();
            if (res.success) {
                setHCCFields(res?.data);
                setCustomFieldIdsForValidation(res?.data?.filter((item) => (item?.validation === 'Mandatory'))?.map(item => item.id));
            } else {
                window.toastr.error(res?.message);
            }
        } catch (error) {
            window.toastr.error(error?.message || "Couldn't fetch custom fields.");
        }
    };

    useEffect(() => {
        let flag = true;
        if (flag) {
            fetchHCCFields();
        }
        return () => flag = false;

        // eslint-disable-next-line
    }, []);

    const renderCustomFieldCells = (field) => {
        const fieldObj = field?.reduce((acc, item) => {
               acc[item.HCCFieldId] = item;
               return acc;
        }, {});
        return hccFields?.map((item) => {
            return (
                <td>
                    {fieldObj[item.id] ? fieldObj[item.id]?.HCCDropdownOptions?.length ? fieldObj[item.id]?.HCCDropdownOptions?.map(option => option?.name).join(', ') : fieldObj[item.id]?.value : ''}
                </td>
            )
        })
    };

    return (
        <>
            <div style={{ overflow: 'hidden' }}>
                <div className="d-flex justify-content-between progress-button">
                    <div className="ms-3">
                        <h3 className="pb-0">HCC Speciality </h3>
                        <h5>
                        </h5>
                    </div>
                </div>

                <div className="card priority-card">
                    <div className="card-header pt-7 border-bottom-0 justify-content-end">
                        <div className="d-flex">
                            <Filter
                                hccDropdownOptions={hccDropdownOptions}
                                setFilters={setFilters}
                                setCurrentPage={setCurrentPage}
                            />

                            <button
                                type="button"
                                className="btn btn-primary ms-4"
                                onClick={() => openAddModal()}
                            >
                                {" "}
                                <i className="fa fa-plus" /> Add{" "}
                            </button>

                            <button
                                type="button"
                                className="btn btn-primary ms-4 btn-sm"
                                onClick={openUploadModal}
                            >
                                {" "}
                                <i className="fa fa-upload" />   Upload{" "} 
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary ms-4 btn-sm"
                                onClick={fetchExportHCCReport}
                            >
                                {" "}
                                <i className="fa fa-file-export" />   Export{" "} {isExporting && <span className="spinner-border spinner-border-sm" />}
                            </button>

                        </div>
                    </div>
                    <div className="card-body  py-2">
                        <div>
                            <div className="separator separator-dashed my-5 mt-0" />
                            <div className="tab-content" id="myTabContent">
                                <div
                                    className="tab-pane fade show active"
                                    id="kt_tab_pane_critical"
                                    role="tabpanel"
                                >
                                    <div className="table-responsive">
                                        <table
                                            className="table align-middle table-row-dashed fs-6 gy-5"
                                            id="kt_table_todo_list"
                                        >
                                            <thead>
                                                <tr className="text-start text-dark fw-bold fs-7 text-uppercase gs-0">
                                                    <th>Member ID{" "}</th>
                                                    <th>Member Name{" "} </th>
                                                    <th> Medicare No </th>
                                                    <th>Date Of Birth</th>
                                                    <th>Coder Name</th>
                                                    <th>Date of Service </th>
                                                    {/* <th>Received Date</th> */}
                                                    <th>Date of Review</th>
                                                    <th>Payor Name</th>
                                                    <th>V24 ICD Code</th>
                                                    <th> V24 ICD Code Description </th>
                                                    <th> V24 HCC Value</th>
                                                    <th> V28 HCC Value</th>
                                                    <th> Validate </th>
                                                    <th> V28 ICD Code </th>
                                                    <th> V28 ICD Code Description </th>
                                                    <th> Reason Code </th>
                                                    <th> Source </th>
                                                    <th> Reviewer Notes</th>
                                                    {hccFields?.map(item => <th key={item.id}>{ item.name}</th>)}
                                                    <th> Action </th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-600 fw-semibold w-100 align-middle text-center">
                                                {hccSpecialityData?.map(item => (
                                                    <tr className="cursor-pointer" key={item?.id}>
                                                        <td>{item?.memberId}</td>
                                                        <td className="text-uppercase" >{item?.memberName}</td>
                                                        <td>{item?.medicareNo}</td>
                                                        <td>{item?.dateOfBirth ? moment(item?.dateOfBirth).format('MM/DD/YYYY') : " "}</td>
                                                        {item?.coderId?.id ? (
                                                            <td>
                                                                <div className="symbol-group symbol-hover flex-nowrap">
                                                                    <OverlayTrigger
                                                                        overlay={<Tooltip>{item?.coderId?.first_name + " " + item?.coderId?.last_name}</Tooltip>}
                                                                        key={item?.coderId?.id}
                                                                        placement={"right"}
                                                                    >
                                                                        <div
                                                                            className="symbol symbol-35px symbol-circle user-image"
                                                                            data-kt-initialized="1"
                                                                        >
                                                                            <img
                                                                                alt="name"
                                                                                src={
                                                                                    item?.coderId?.image_url ??
                                                                                    `../assets/media/avatars/300-${imageIdGenerate(
                                                                                        item?.coderId?.id,
                                                                                    )}.jpg`
                                                                                }
                                                                            />
                                                                        </div>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            </td>
                                                        ) : (
                                                            <td>{" "}</td>
                                                        )}
                                                        <td>{item?.dateOfService ? moment(item?.dateOfService).format('MM/DD/YYYY') : " "}</td>
                                                        {/* <td>{item?.receivedDate ? moment(item?.receivedDate).format('MM/DD/YYYY') : " "}</td> */}
                                                        <td>{item?.reviewDate ? moment(item?.reviewDate).format('MM/DD/YYYY') : " "}</td>
                                                        <td>{item?.HCCPayor?.name }</td>
                                                        <td>{item?.originalCode}</td>
                                                        <td>{item?.originalCodeDescription}</td>
                                                        <td> {item?.v24}</td>
                                                        <td>{item?.v28 || "N/A"}</td>
                                                        <td>{item?.Validate?.name}</td>
                                                        <td>{item?.revisedCode}</td>
                                                        <td>{item?.revisedCodeDescription}</td>
                                                        <td>{item?.ReasonCode?.name}</td>
                                                        <td>{item?.Source?.name}</td>
                                                        <td>{item?.reviewerNotes}</td>
                                                        {renderCustomFieldCells(item?.customFields)}
                                                        <td>
                                                            <div className="d-flex gap-2 justify-content-center align-items-center h-100">
                                                                {(role === userRole.TL || role === userRole.Manager) ?
                                                                    <button
                                                                        style={{ border: 'none', backgroundColor: 'transparent' }}
                                                                        aria-label="button"
                                                                        onClick={(e) => {
                                                                            if ((deletingRecordId && deletingRecordId === item?.id)) return;
                                                                            e.stopPropagation();
                                                                            handleDeleteRecord(item?.id)
                                                                        }}
                                                                    >
                                                                        {(deletingRecordId && deletingRecordId === item?.id) ?
                                                                            <span className="spinner-border spinner-border-sm align-middle ms-2" />
                                                                            : <i className="fa fa-trash" />}
                                                                    </button>
                                                                    :
                                                                    <></>
                                                                }
                                                                <button
                                                                    style={{ border: 'none', backgroundColor: 'transparent' }}
                                                                    aria-label="button"
                                                                >
                                                                    <i className="fa fa-edit" onClick={() => {
                                                                        setSelectedRow(item?.id);
                                                                        setHCCFieldsValue(item?.customFields);
                                                                        openEditModal(item);
                                                                    }} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {hccSpecialityData?.length > 0
                                            ? (
                                                <CustomPagination
                                                    dataPerPage={size}
                                                    totalData={totalCount}
                                                    currentPage={currentPage}
                                                    paginate={setCurrentPage}
                                                />
                                            )
                                            : <div className="text-center fw-bold mx-auto m-4">No Records Found!</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showAddModal && (<AddModal
                isOpen={showAddModal}
                closeModal={closeAddModal}
                setInvalidateList={setInvalidateList}
                hccDropdownOptions={hccDropdownOptions}
                setCurrentPage={setCurrentPage}
                userDetail={user}
                hccFields={hccFields}
                customFieldIdsForValidation={customFieldIdsForValidation}
            />)}
            
            {selectedRow && showEditModal &&
                <EditModal
                    isOpen={showEditModal}
                    closeModal={closeEditModal}
                    setInvalidateList={setInvalidateList}
                    id={selectedRow}
                    hccDropdownOptions={hccDropdownOptions}
                    setCurrentPage={setCurrentPage}
                    userDetail={user}
                    hccFields={hccFields}
                    hccFieldsValue={hccFieldsValue}
                    customFieldIdsForValidation={customFieldIdsForValidation}
                />

            }
            {
                showUploadModal && (
                    <UploadReportModal
                        isShow={showUploadModal}
                        onHide={closeUploadModal}
                        setInvalidateList={setInvalidateList}
                        setCurrentPage={setCurrentPage}
                    />
              )
            }
        </>
    )
}

export default HCCProject;